export type StrategyType = 'mobile' | 'desktop'

export class ReportRequestData {
  url: string = ''
  strategy: StrategyType = 'mobile'
  email: string = ''

  constructor(values: Partial<ReportRequestData> = {}) {
    Object.assign(this, values)
  }
}

export type ReportStatus = 'done' | 'processing' | 'error'

export class Lighthouse56Audit {
  static clampTo2Decimals(val: number) {
    return Math.round(val * 100) / 100
  }

  static erf(x: number) {
    // erf(-x) = -erf(x);
    const sign = Math.sign(x)
    x = Math.abs(x)

    const a1 = 0.254829592
    const a2 = -0.284496736
    const a3 = 1.421413741
    const a4 = -1.453152027
    const a5 = 1.061405429
    const p = 0.3275911
    const t = 1 / (1 + p * x)
    const y = t * (a1 + t * (a2 + t * (a3 + t * (a4 + t * a5))))
    return sign * (1 - y * Math.exp(-x * x))
  }

  static getLogNormalDistribution(median: number, falloff: number) {
    const location = Math.log(median)

    const logRatio = Math.log(falloff / median)
    const shape = Math.sqrt(1 - 3 * logRatio - Math.sqrt((logRatio - 3) * (logRatio - 3) - 8)) / 2

    return {
      computeComplementaryPercentile: (x: number) => {
        const standardizedX = (Math.log(x) - location) / (Math.SQRT2 * shape)
        return (1 - this.erf(standardizedX)) / 2
      },
    }
  }

  static computeScore(value: number, podr: number, median: number): number {
    const distribution = this.getLogNormalDistribution(
      median,
      podr,
    )

    let score = distribution.computeComplementaryPercentile(value)
    score = Math.min(1, score)
    score = Math.max(0, score)
    return this.clampTo2Decimals(score)
  }
}

export type AuditScore = {
  score: number
  weight: number
  value: number
  targets: {
    for0: number,
    for50: number,
    for90: number,
    for100: number,
  },
  scoringParams: {
    median: number,
    podr: number,
  }
}

export class PageSpeedDigest extends ReportRequestData {
  finalUrl: string = ''
  score: number = 0
  scores: { [key: string]: AuditScore } = {}
  metrics: { [key: string]: number } = {}
  simulation: { [key: string]: number } = {}
  screenshot: string = ''

  constructor(values: Partial<PageSpeedDigest> = {}) {
    super(values)
    Object.assign(this, values)
  }

  get intScore100() {
    return Math.round(this.score * 100)
  }

  get simulatedScore() {
    const scores: { [key: string]: number } = {}
    for (let key in this.simulation) {
      scores[key] = Lighthouse56Audit.computeScore(this.simulation[key], this.scores[key].scoringParams.podr, this.scores[key].scoringParams.median)
    }
    return this.simulation
  }

  static scoreCssClass(score: number) {
    if (score < 0.5) {
      return 'score-low'
    } else if (score < 0.9) {
      return 'score-middle'
    } else {
      return 'score-high'
    }
  }
}

export class Audit {
  id: string = ''
  name: string = ''
  isTime: boolean = true
  group: string = ''
  b4Color: string = 'primary'
  important: boolean = false
  description: string = ''
  causes: string[] = []

  constructor(values: Partial<Audit> = {}) {
    if (values.id !== undefined) this.id = values.id
    if (values.name !== undefined) this.name = values.name
    if (values.isTime !== undefined) this.isTime = values.isTime
    if (values.group !== undefined) this.group = values.group
    if (values.b4Color !== undefined) this.b4Color = values.b4Color
    if (values.important !== undefined) this.important = values.important
    if (values.description !== undefined) this.description = values.description
    if (values.causes !== undefined) this.causes = values.causes
    if (values.causes !== undefined) this.causes = values.causes
  }

  static get all(): Array<Audit> {
    return [
      { id: 'speedIndex', important: true, name: '速度インデックス', group: '体感表示時間', b4Color: 'danger', description: 'リクエスト開始からファーストビュー完成までの時間。準備できた要素から表示し、その要素が大きいほど評価が高い。', causes: ['HTMLの応答時間', 'First Contentful Paint', 'CSSの応答時間', 'メイン画像の応答時間', 'JavaScriptファイルの軽さ'] },
      { id: 'largestContentfulPaint', name: '最大コンテンツの描画', group: '体感表示時間', b4Color: 'danger', description: 'HTMLのサーバー応答から、最も大きな領域を占めるテキスト要素または画像要素が描画されるまでの時間。', causes: ['script要素の適切な記述', 'HTMLファイルの軽さ', 'CSSファイルの軽さ', 'メイン画像の軽さ'] },
      { id: 'firstContentfulPaint', name: 'First Contentful Paint', group: 'レンダリングの初速', b4Color: 'info', description: 'HTMLのサーバー応答から最初の要素が描画されるまでの時間。', causes: ['script要素の適切な記述', 'HTMLファイルの軽さ', 'CSSファイルの軽さ'] },
      { id: 'interactive', name: 'インタラクティブになるまでの時間', group: 'JavaScriptの負荷', b4Color: 'success', description: 'FCP+5秒以後、ページ読み込み時の高負荷が終息し、メインスレッドに5秒以上の余裕が生まれるまでの時間。ロングタスク(50ms以上メインスレッドを専有する処理)の数と長さが最も影響。', causes: ['First Contentful Paint', 'ロングタスクの少なさ', 'script要素の適切な記述', 'HTMLファイルの軽さ', 'CSSファイルの軽さ', 'JavaScriptファイルの軽さ'] },
      { id: 'totalBlockingTime', name: '合計ブロック時間', group: 'JavaScriptの負荷', b4Color: 'success', description: 'ロングタスク(50ms以上を要する主にJavaScript)の数と長さ。ロングタスクの50msを超えた部分の合計時間で計算。', causes: ['ロングタスクの少なさ', 'HTMLファイルの軽さ'] },
      { id: 'cumulativeLayoutShift', name: '累積レイアウト変更', isTime: false, group: '誤操作の誘引', b4Color: 'secondary', description: 'レイアウトの段階的な変化と影響を受ける領域の大きさ。画像の読み込み完了やJavaScriptにより後からの要素が挿入されレイアウトがズレる事象が典型例。ちなみにスピードとは無関係。', causes: ['JavaScriptによる要素の挿入', '画像領域のサイズ指定漏れ'] },
    ].map(v => new Audit(v))
  }
}